function normalizeStatus(value = "") {
  const status = String(value || "").trim();
  return ["todo", "doing", "done", "later"].includes(status) ? status : "todo";
}

function doneForStatus(status = "todo") {
  return normalizeStatus(status) === "done";
}

function clonePlanner(planner = []) {
  return (Array.isArray(planner) ? planner : []).map((item) => ({ ...item }));
}

function cleanGroupDerivedFields(group = {}) {
  const next = { ...group };
  delete next.status;
  delete next.date;
  delete next.done;
  delete next.completedAt;
  return next;
}

export function migratePlannerDerivedFields(target = {}) {
  const sourcePlanner = Array.isArray(target?.planner) ? target.planner : [];
  let changed = false;
  const planner = sourcePlanner.map((item) => {
    if (!item?.isGroup) return item;
    const hasDerived = ["status", "date", "done", "completedAt"].some((field) => Object.hasOwn(item, field));
    if (!hasDerived) return item;
    changed = true;
    return cleanGroupDerivedFields(item);
  });
  if (!changed) return target;
  return { ...target, planner };
}

export function createPlannerItem(planner = [], input = {}, options = {}) {
  const id = String(input.id || options.idFactory?.() || "").trim();
  if (!id) return { planner: clonePlanner(planner), item: null, changed: false };
  const done = Boolean(input.done);
  const item = {
    ...input,
    id,
    done,
    status: done ? "done" : normalizeStatus(input.status),
  };
  if (!done) delete item.completedAt;
  return {
    planner: [...clonePlanner(planner), item],
    item,
    changed: true,
  };
}

export function movePlannerItemToColumn(planner = [], id = "", status = "todo", options = {}) {
  const cleanId = String(id || "").trim();
  const nextStatus = normalizeStatus(status);
  let changed = false;
  const nextPlanner = clonePlanner(planner).map((item) => {
    if (item.id !== cleanId || item.isGroup) return item;
    const done = doneForStatus(nextStatus);
    const next = { ...item, status: nextStatus, done };
    if (done) next.completedAt = options.completedAt || item.completedAt || options.todayValue?.() || "";
    else delete next.completedAt;
    changed = JSON.stringify(next) !== JSON.stringify(item);
    return next;
  });
  return { planner: nextPlanner, changed };
}

export function reschedulePlannerItem(planner = [], id = "", schedule = {}) {
  const cleanId = String(id || "").trim();
  const date = String(schedule.date || "").trim();
  if (!cleanId || !date) return { planner: clonePlanner(planner), changed: false };
  let changed = false;
  const nextPlanner = clonePlanner(planner).map((item) => {
    if (item.id !== cleanId || item.isGroup) return item;
    const next = { ...item, date };
    const time = String(schedule.time || "").trim();
    const endTime = String(schedule.endTime || "").trim();
    if (time) next.time = time;
    else delete next.time;
    if (endTime) next.endTime = endTime;
    else delete next.endTime;
    changed = JSON.stringify(next) !== JSON.stringify(item);
    return next;
  });
  return { planner: nextPlanner, changed };
}

export function togglePlannerItemDone(planner = [], id = "", done = false, options = {}) {
  const cleanId = String(id || "").trim();
  const nextDone = Boolean(done);
  let changed = false;
  const nextPlanner = clonePlanner(planner).map((item) => {
    if (item.id !== cleanId || item.isGroup) return item;
    const next = {
      ...item,
      done: nextDone,
      status: nextDone ? "done" : item.status === "done" ? "todo" : normalizeStatus(item.status),
    };
    if (nextDone) next.completedAt = options.completedAt || item.completedAt || options.todayValue?.() || "";
    else delete next.completedAt;
    changed = JSON.stringify(next) !== JSON.stringify(item);
    return next;
  });
  return { planner: nextPlanner, changed };
}

export function groupItems(planner = [], ids = [], groupInput = {}, options = {}) {
  const selectedIds = [...new Set(ids.map((id) => String(id || "").trim()).filter(Boolean))];
  const source = clonePlanner(planner);
  const selected = source.filter((item) => selectedIds.includes(item.id) && !item.isGroup);
  if (selected.length < 2) return { planner: source, group: null, changed: false };
  const dates = [...new Set(selected.map((item) => String(item.date || "").trim()))];
  if (dates.length !== 1) return { planner: source, group: null, changed: false };
  const group = cleanGroupDerivedFields({
    id: String(groupInput.id || options.idFactory?.() || "").trim(),
    title: groupInput.title || `Grupo com ${selected.length} tarefas`,
    type: "Grupo",
    isGroup: true,
    collapsed: Boolean(groupInput.collapsed),
    subject: groupInput.subject || "Geral",
    subjects: groupInput.subjects || [],
    order: groupInput.order,
  });
  if (!group.id) return { planner: source, group: null, changed: false };
  const plannerWithChildren = source.map((item) => (
    selectedIds.includes(item.id) ? { ...item, plannerGroupId: group.id } : item
  ));
  return { planner: [...plannerWithChildren, group], group, changed: true };
}

export function ungroupGroup(planner = [], groupId = "") {
  const cleanId = String(groupId || "").trim();
  if (!cleanId) return { planner: clonePlanner(planner), changed: false };
  let changed = false;
  const nextPlanner = clonePlanner(planner)
    .filter((item) => {
      if (item.id !== cleanId) return true;
      changed = true;
      return false;
    })
    .map((item) => {
      if (item.plannerGroupId !== cleanId) return item;
      changed = true;
      const next = { ...item };
      delete next.plannerGroupId;
      return next;
    });
  return { planner: nextPlanner, changed };
}

export function deletePlannerItem(planner = [], id = "") {
  const cleanId = String(id || "").trim();
  const source = clonePlanner(planner);
  const item = source.find((entry) => entry.id === cleanId);
  if (!item) return { planner: source, deletedIds: [], changed: false };
  const deletedIds = item.isGroup
    ? [cleanId, ...source.filter((entry) => entry.plannerGroupId === cleanId).map((entry) => entry.id)]
    : [cleanId];
  return {
    planner: source.filter((entry) => !deletedIds.includes(entry.id)),
    deletedIds,
    changed: true,
  };
}
